import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, InputGroup, Modal } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import { postPlantillaProcedimiento, putPlantillaProcedimiento } from "../../../api/common";
import { SelectMultPatron } from "../../Items/SelectMultPatron";

// Formulario para crear/editar plantilla
const PlantillaForm = ({ plantilla, onSuccess, onCancel }) => {
  const [codigo, setCodigo] = useState(plantilla?.codigo || "");
  const [descripcion, setDescripcion] = useState(plantilla?.descripcion || "");
  const [tipos, setTipos] = useState(plantilla?.tiposEquipoAplicable || []);
  const [tagInput, setTagInput] = useState("");
  const [cantPatronesRequeridos, setCantPatronesRequeridos] = useState(plantilla?.cantPatronesRequeridos || 1);
  const [magnitudes, setMagnitudes] = useState(plantilla?.magnitudesConfig && plantilla.magnitudesConfig.length>0 ? plantilla.magnitudesConfig : [{ 
    nombre: "", 
    unidad: "", 
    cantPuntos: 1, 
    puntosCalibracion: [''],
    cantMedicionesPorPunto: 1, 
    requiereEstabilidad: false, 
    cantMedicionesEstabilidad: 1,
    requiereHisteresis: false,
    histeresisEnTodosLosPuntos: false,
    puntoHisteresis: 0,
    requiereMedioCalibracion: false,
    patronesCalibracion: []
  }]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setCodigo(plantilla?.codigo || "");
    setDescripcion(plantilla?.descripcion || "");
    setTipos(plantilla?.tiposEquipoAplicable || []);
    setCantPatronesRequeridos(plantilla?.cantPatronesRequeridos || 1);
    setMagnitudes(plantilla?.magnitudesConfig && plantilla.magnitudesConfig.length>0 ? plantilla.magnitudesConfig : [{ 
      nombre: "", 
      unidad: "", 
      cantPuntos: 1, 
      puntosCalibracion: [''],
      cantMedicionesPorPunto: 1, 
      requiereEstabilidad: false, 
      cantMedicionesEstabilidad: 1,
      requiereHisteresis: false,
      histeresisEnTodosLosPuntos: false,
      puntoHisteresis: 0,
      requiereMedioCalibracion: false,
      patronesCalibracion: []
    }]);
  }, [plantilla]);

  // Agrega tag cuando presiona Enter
  const handleTagKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = tagInput.trim();
      if (val && !tipos.includes(val)) {
        setTipos([...tipos, val]);
      }
      setTagInput("");
    }
  };

  const removeTag = (t) => setTipos(tipos.filter((x) => x !== t));

  const addMagnitud = () => {
    // Heredar configuración de la primera magnitud si existe
    const primeraM = magnitudes.length > 0 ? magnitudes[0] : null;
    const patronesHeredados = primeraM ? primeraM.patronesCalibracion : [];
    let medioHeredado = primeraM ? primeraM.medioCalibracion : undefined;
    if (!Array.isArray(medioHeredado) || medioHeredado.length === 0) medioHeredado = undefined;
    const requiereMedio = primeraM ? primeraM.requiereMedioCalibracion : false;

    setMagnitudes([
      ...magnitudes,
      {
        nombre: "",
        unidad: "",
        cantPuntos: 1,
        puntosCalibracion: [''],
        cantMedicionesPorPunto: 1,
        requiereEstabilidad: false,
        cantMedicionesEstabilidad: 1,
        requiereHisteresis: false,
        histeresisEnTodosLosPuntos: false,
        puntoHisteresis: 0,
        requiereMedioCalibracion: requiereMedio,
        medioCalibracion: medioHeredado ? [...medioHeredado] : undefined,
        patronesCalibracion: [...patronesHeredados]
      }
    ]);
  };

  const removeMagnitud = (idx) => {
    if (magnitudes.length === 1) return; // mínimo 1
    setMagnitudes(magnitudes.filter((_, i) => i !== idx));
  };

  const handleMagnitudChange = (idx, key, value) => {
    const copy = [...magnitudes];
    copy[idx] = { ...copy[idx], [key]: value };
    
    // Si cambia cantPuntos, ajustar array de puntosCalibracion
    if (key === 'cantPuntos') {
      const newSize = Number(value);
      const currentPoints = copy[idx].puntosCalibracion || [];
      if (newSize > currentPoints.length) {
        // Agregar puntos vacíos
        copy[idx].puntosCalibracion = [...currentPoints, ...Array(newSize - currentPoints.length).fill('')];
      } else {
        // Recortar puntos
        copy[idx].puntosCalibracion = currentPoints.slice(0, newSize);
      }
    }
    
    setMagnitudes(copy);
  };

  const handlePuntoCalibracionChange = (magnitudIdx, puntoIdx, value) => {
    const copy = [...magnitudes];
    if (!copy[magnitudIdx].puntosCalibracion) {
      copy[magnitudIdx].puntosCalibracion = [];
    }
    copy[magnitudIdx].puntosCalibracion[puntoIdx] = value === '' ? '' : Number(value);
    setMagnitudes(copy);
  };

  const validate = () => {
    const e = {};
    if (!cantPatronesRequeridos || cantPatronesRequeridos < 1) e.cantPatronesRequeridos = "Mínimo 1";
    if (!magnitudes || magnitudes.length === 0) e.magnitudes = "Al menos una magnitud requerida";
    magnitudes.forEach((m, i) => {
      if (!m.nombre || m.nombre.trim() === "") e[`m_nombre_${i}`] = "Requerido";
      if (!m.unidad || m.unidad.trim() === "") e[`m_unidad_${i}`] = "Requerido";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const payload = {
      codigo,
      descripcion,
      tiposEquipoAplicable: tipos,
      cantPatronesRequeridos,
      magnitudesConfig: magnitudes,
    };
    try {
      if (plantilla && (plantilla.id || plantilla._id)) {
        await putPlantillaProcedimiento(plantilla.id || plantilla._id, payload);
      } else {
        await postPlantillaProcedimiento(payload);
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrors({ submit: err.message || "Error al guardar" });
    } finally {
      setSaving(false);
    }
  };

  const handlemedioCalibracionChange = (index, event) => {
    const copy = [...magnitudes];
    // SelectMultPatron pasa un evento con {target: {value: array}}
    const nuevosValores = event.target.value || [];
    
    // Limitar según cantPatronesRequeridos
    if (nuevosValores.length > cantPatronesRequeridos) {
      alert(`Solo puede seleccionar hasta ${cantPatronesRequeridos} patrón(es) para medio de calibración`);
      return;
    }
    
    copy[index].medioCalibracion = nuevosValores;
    setMagnitudes(copy);
  }

  const handlePatronesCalibracionChange = (index, event) => {
    const copy = [...magnitudes];
    // SelectMultPatron pasa un evento con {target: {value: array}}
    const nuevosValores = event.target.value || [];
    
    // Limitar según cantPatronesRequeridos
    if (nuevosValores.length > cantPatronesRequeridos) {
      alert(`Solo puede seleccionar hasta ${cantPatronesRequeridos} patrón(es) de calibración`);
      return;
    }
    
    copy[index].patronesCalibracion = nuevosValores;
    setMagnitudes(copy);
  }

  return (
    <Form onSubmit={handleSubmit} 
    style={{ maxHeight: "80vh", overflowY: "auto", padding: "10px" }}>

      {/* Sección: Información General */}
      <div className="mb-4">
        <h5 className="mb-3" style={{ color: "#0d6efd", borderBottom: "2px solid #0d6efd", paddingBottom: "8px" }}>Información General</h5>
        <Row className="mb-3">
          <Col md={8}>
            <Form.Group className="mb-2">
              <Form.Label>Descripción</Form.Label>
              <Form.Control 
                value={descripcion} 
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ingrese la descripción de la plantilla"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Patrones requeridos *</Form.Label>
              <Form.Control 
                type="number" 
                min={1} 
                value={cantPatronesRequeridos} 
                onChange={(e) => setCantPatronesRequeridos(Number(e.target.value))} 
                isInvalid={!!errors.cantPatronesRequeridos} 
              />
              <Form.Control.Feedback type="invalid">{errors.cantPatronesRequeridos}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Sección: Magnitudes */}
      <div className="mb-4">
        <h5 className="mb-3" style={{ color: "#0d6efd", borderBottom: "2px solid #0d6efd", paddingBottom: "8px" }}>Configuración de Magnitudes</h5>
        {errors.magnitudes && <div className="alert alert-danger">{errors.magnitudes}</div>}
        {magnitudes.map((m, i) => (
        <div key={i} className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0" style={{ color: "#495057" }}>Magnitud {i + 1}</h6>
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={() => removeMagnitud(i)} 
              disabled={magnitudes.length === 1}
              title="Eliminar magnitud"
            >
              <FaTrash /> Eliminar
            </Button>
          </div>

          {/* Información básica */}
          <div className="bg-white p-3 rounded mb-3">
            <h6 className="text-muted mb-3" style={{ fontSize: "0.9rem", fontWeight: "600" }}>Información Básica</h6>
            <Row className="align-items-end">
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control 
                    value={m.nombre} 
                    onChange={(e) => handleMagnitudChange(i, "nombre", e.target.value)} 
                    isInvalid={!!errors[`m_nombre_${i}`]}
                    placeholder="Ej: Temperatura"
                  />
                  <Form.Control.Feedback type="invalid">{errors[`m_nombre_${i}`]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Unidad *</Form.Label>
                  <Form.Control 
                    value={m.unidad} 
                    onChange={(e) => handleMagnitudChange(i, "unidad", e.target.value)} 
                    isInvalid={!!errors[`m_unidad_${i}`]}
                    placeholder="Ej: °C"
                  />
                  <Form.Control.Feedback type="invalid">{errors[`m_unidad_${i}`]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Cant. Puntos</Form.Label>
                  <Form.Control 
                    type="number" 
                    min={1} 
                    value={m.cantPuntos} 
                    onChange={(e) => handleMagnitudChange(i, "cantPuntos", Number(e.target.value))} 
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Med./punto</Form.Label>
                  <Form.Control 
                    type="number" 
                    min={1} 
                    value={m.cantMedicionesPorPunto} 
                    onChange={(e) => handleMagnitudChange(i, "cantMedicionesPorPunto", Number(e.target.value))} 
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Puntos de calibración */}
          <div className="bg-white p-3 rounded mb-3">
            <h6 className="text-muted mb-3" style={{ fontSize: "0.9rem", fontWeight: "600" }}>Puntos de Calibración</h6>
            <Row>
              {(m.puntosCalibracion || ['']).map((punto, pIdx) => (
                <Col md={3} key={pIdx} className="mb-2">
                  <Form.Group>
                    <Form.Label className="small">Punto {pIdx + 1}</Form.Label>
                    <Form.Control 
                      type="number" 
                      step="any"
                      value={punto} 
                      onChange={(e) => handlePuntoCalibracionChange(i, pIdx, e.target.value)}
                      placeholder={`Ej: ${pIdx * 10 + 10}`}
                    />
                  </Form.Group>
                </Col>
              ))}
            </Row>
          </div>

          {/* Configuración avanzada */}
          <div className="bg-white p-3 rounded mb-3">
            <h6 className="text-muted mb-3" style={{ fontSize: "0.9rem", fontWeight: "600" }}>Configuración Avanzada</h6>
            
            {/* Histéresis */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Check 
                  type="switch" 
                  label="Requiere histéresis" 
                  checked={m.requiereHisteresis || false} 
                  onChange={(e) => handleMagnitudChange(i, "requiereHisteresis", e.target.checked)} 
                />
              </Col>
              {m.requiereHisteresis && (
                <>
                  <Col md={4}>
                    <Form.Check 
                      type="switch" 
                      label="Histéresis en todos los puntos" 
                      checked={m.histeresisEnTodosLosPuntos || false} 
                      onChange={(e) => handleMagnitudChange(i, "histeresisEnTodosLosPuntos", e.target.checked)} 
                    />
                  </Col>
                  {!m.histeresisEnTodosLosPuntos && (
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Punto de histéresis</Form.Label>
                        <Form.Control 
                          type="number" 
                          step="any"
                          value={m.puntoHisteresis || 0} 
                          onChange={(e) => handleMagnitudChange(i, "puntoHisteresis", Number(e.target.value))} 
                          placeholder="Ej: 50"
                        />
                      </Form.Group>
                    </Col>
                  )}
                </>
              )}
            </Row>

            {/* Estabilidad */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Check 
                  type="switch" 
                  label="Requiere estabilidad" 
                  checked={m.requiereEstabilidad} 
                  onChange={(e) => handleMagnitudChange(i, "requiereEstabilidad", e.target.checked)} 
                />
              </Col>
              {m.requiereEstabilidad && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Mediciones estabilidad</Form.Label>
                    <Form.Control 
                      type="number" 
                      min={1} 
                      value={m.cantMedicionesEstabilidad} 
                      onChange={(e) => handleMagnitudChange(i, "cantMedicionesEstabilidad", Number(e.target.value))} 
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            {/* Medio de calibración */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Check 
                  type="switch" 
                  label="Requiere medio de calibración" 
                  checked={m.requiereMedioCalibracion} 
                  onChange={(e) => handleMagnitudChange(i, "requiereMedioCalibracion", e.target.checked)} 
                />
              </Col>
              {m.requiereMedioCalibracion && (
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Patrones medio de calibración</Form.Label>
                    <SelectMultPatron
                      dataDefault={m.medioCalibracion || []}
                      handledata={(event) => handlemedioCalibracionChange(i, event)}
                      name={`medioCalibracion_${i}`}
                      collection={"Patrones"}
                      field={"item"}
                      excludeIds={[]}
                    />
                    <Form.Text className="text-muted">
                      Máximo {cantPatronesRequeridos} patrón(es) seleccionado(s): {m.medioCalibracion?.length || 0}/{cantPatronesRequeridos}
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </div>

          {/* Patrones de calibración */}
          <div className="bg-white p-3 rounded">
            <h6 className="text-muted mb-3" style={{ fontSize: "0.9rem", fontWeight: "600" }}>Patrones de Calibración</h6>
            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Seleccionar patrones</Form.Label>
                  <SelectMultPatron
                    dataDefault={m.patronesCalibracion || []}
                    handledata={(event) => handlePatronesCalibracionChange(i, event)}
                    name={`patronesCalibracion_${i}`}
                    collection={"Patrones"}
                    field={"item"}
                    excludeIds={[]}
                  />
                  <Form.Text className="text-muted">
                    Máximo {cantPatronesRequeridos} patrón(es) seleccionado(s): {m.patronesCalibracion?.length || 0}/{cantPatronesRequeridos}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>
    
        </div>
      ))}

        <div className="mb-4">
          <Button variant="primary" size="sm" onClick={addMagnitud}>
            <FaPlus /> Agregar nueva magnitud
          </Button>
          {magnitudes.length > 1 && (
            <small className="text-muted ms-3">
              Las nuevas magnitudes heredarán los patrones de la primera magnitud
            </small>
          )}
        </div>
      </div>

      {/* Botones de acción */}
      {errors.submit && <div className="alert alert-danger mb-3">{errors.submit}</div>}

      <div className="text-end pt-3" style={{ borderTop: "1px solid #dee2e6" }}>
        <Button variant="secondary" className="me-2" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Guardar Plantilla"}
        </Button>
      </div>
    </Form>
  );
};

export default PlantillaForm;
